import { useState, useEffect } from 'react';
import { X, BookOpen, Users, CheckCircle, Trophy, HelpCircle, ChevronRight, Lightbulb } from 'lucide-react';
import { Button } from '../../ui/button';

/**
 * Lesson Guide Popup - Shows users what steps are needed to complete a lesson
 * Can be triggered on first visit or via help button
 */
export const LessonGuide = ({ 
  topic, 
  isOpen, 
  onClose, 
  simulationRequirements,
  currentStep = 1 
}) => {
  if (!isOpen) return null;

  const isPhase4 = topic?.phase_id === 'phase_4';
  const hasSimulations = simulationRequirements?.has_simulations;
  const hasAssessment = !!topic?.mock_test_id;
  const hasTypingTest = !!topic?.typing_test_number;

  // Build steps based on topic requirements
  const steps = [];
  
  // Step 1: Lesson content
  steps.push({
    number: 1,
    title: 'Complete Lesson Content',
    description: 'Listen to all voice lessons and review key concepts',
    icon: BookOpen,
    type: 'lesson'
  });

  // Step 2: Simulations (Phase 4 only)
  if (hasSimulations) {
    const simCount = simulationRequirements?.total_required || 2;
    const completedCount = simulationRequirements?.completed_count || 0;
    steps.push({
      number: 2,
      title: `Complete ${simCount} Recruiter Simulations`,
      description: `Practice with AI recruiters - Easy first, then Medium. Score 80+ on each. (${completedCount}/${simCount} done)`,
      icon: Users,
      type: 'simulation',
      progress: { completed: completedCount, total: simCount }
    });
  }

  // Step 3 or 2: Typing Test (if exists)
  if (hasTypingTest) {
    steps.push({
      number: steps.length + 1,
      title: 'Pass Typing Test',
      description: 'Test your typing speed and accuracy',
      icon: CheckCircle,
      type: 'typing'
    });
  }

  // Assessment step
  if (hasAssessment) {
    steps.push({
      number: steps.length + 1,
      title: 'Pass the Assessment',
      description: 'Answer questions to test your understanding. Score 80%+ to complete.',
      icon: CheckCircle,
      type: 'assessment'
    });
  }

  // Final step
  steps.push({
    number: steps.length + 1,
    title: 'Topic Complete!',
    description: 'Move to the next lesson in your learning path',
    icon: Trophy,
    type: 'complete'
  });

  const tips = [
    'Take your time with each step - quality matters more than speed',
    'If you score below 80%, review the lesson content before retrying',
    'You can always come back and retake simulations to improve your score'
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Lesson Guide</h2>
                <p className="text-emerald-100 text-sm">{topic?.code}: {topic?.title}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="close-guide-btn"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 overflow-y-auto max-h-[60vh]">
          {/* Steps */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">
              Steps to Complete
            </h3>
            <div className="space-y-2">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isActive = currentStep === step.number;
                const isCompleted = currentStep > step.number;
                
                return (
                  <div 
                    key={step.number}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg border transition-all
                      ${isActive 
                        ? 'bg-emerald-500/10 border-emerald-500/30' 
                        : isCompleted 
                          ? 'bg-slate-800/50 border-slate-700/50 opacity-60'
                          : 'bg-slate-800/30 border-slate-700/30'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                      ${isActive 
                        ? 'bg-emerald-500 text-white' 
                        : isCompleted 
                          ? 'bg-emerald-600/50 text-emerald-400'
                          : 'bg-slate-700 text-slate-400'
                      }
                    `}>
                      {isCompleted ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <span className="text-sm font-bold">{step.number}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${isActive ? 'text-emerald-400' : 'text-slate-500'}`} />
                        <h4 className={`font-medium ${isActive ? 'text-white' : 'text-slate-300'}`}>
                          {step.title}
                        </h4>
                      </div>
                      <p className="text-sm text-slate-400 mt-0.5">{step.description}</p>
                      
                      {/* Progress bar for simulations */}
                      {step.progress && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all"
                              style={{ width: `${(step.progress.completed / step.progress.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <ChevronRight className="h-5 w-5 text-emerald-400 animate-pulse" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-400">Tips for Success</span>
            </div>
            <ul className="space-y-1">
              {tips.map((tip, idx) => (
                <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500"
            data-testid="got-it-btn"
          >
            Got it, let&apos;s start!
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LessonGuide;
